/**
 * AccountantDocChecklist — Per-client document checklist with request functionality.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, FileCheck, Loader2, Send, CheckCircle2, Clock,
  AlertCircle, XCircle, RefreshCw, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  received:     { label: 'Received',     icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
  missing:      { label: 'Missing',      icon: AlertCircle,  color: 'text-red-400',     bg: 'bg-red-500/10 border-red-500/20' },
  requested:    { label: 'Requested',    icon: Clock,        color: 'text-amber-400',   bg: 'bg-amber-500/10 border-amber-500/20' },
  not_required: { label: 'Not Required', icon: XCircle,      color: 'text-slate-500',   bg: 'bg-slate-500/10 border-slate-500/20' },
};

const ChecklistItem = ({ item, onStatusChange, onRequest }) => {
  const [requesting, setRequesting] = useState(false);
  const [customMsg, setCustomMsg] = useState('');
  const [showRequest, setShowRequest] = useState(false);
  const config = STATUS_CONFIG[item.status] || STATUS_CONFIG.missing;
  const Icon = config.icon;

  const handleRequest = async () => {
    setRequesting(true);
    try {
      await onRequest(item.key, customMsg);
      setShowRequest(false);
      setCustomMsg('');
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className={`p-3 rounded-lg border ${config.bg} transition-colors`} data-testid={`checklist-item-${item.key}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Icon className={`h-5 w-5 shrink-0 ${config.color}`} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-white">{item.label}</p>
            <p className="text-xs text-slate-500 truncate">{item.description}</p>
            {item.auto_detected && item.status === 'received' && (
              <p className="text-[10px] text-emerald-500 mt-0.5">Auto-detected from uploads</p>
            )}
            {item.requested_at && item.status === 'requested' && (
              <p className="text-[10px] text-amber-500 mt-0.5">
                Requested {new Date(item.requested_at).toLocaleDateString('en-GB')}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <select
            value={item.status}
            onChange={(e) => onStatusChange(item.key, e.target.value)}
            className="bg-slate-800 border border-slate-600 text-xs text-white rounded px-2 py-1 h-7"
            data-testid={`status-select-${item.key}`}
          >
            <option value="missing">Missing</option>
            <option value="received">Received</option>
            <option value="requested">Requested</option>
            <option value="not_required">Not Required</option>
          </select>
          {item.status !== 'received' && item.status !== 'not_required' && (
            <Button size="sm" variant="ghost"
              onClick={() => setShowRequest(!showRequest)}
              className="h-7 px-2 text-sky-400 hover:text-sky-300 hover:bg-sky-500/10"
              title="Request from client"
              data-testid={`request-btn-${item.key}`}
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      {showRequest && (
        <div className="mt-2 flex items-center gap-2" onClick={e => e.stopPropagation()}>
          <Input
            value={customMsg}
            onChange={e => setCustomMsg(e.target.value)}
            placeholder="Add a note (optional)..."
            className="bg-slate-800 border-slate-600 text-white h-8 text-xs flex-1"
            data-testid={`request-msg-${item.key}`}
          />
          <Button size="sm" onClick={handleRequest} disabled={requesting}
            className="bg-sky-600 hover:bg-sky-700 h-8 px-3 text-xs" data-testid={`send-request-${item.key}`}>
            {requesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
            Send
          </Button>
        </div>
      )}
    </div>
  );
};

const AccountantDocChecklist = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taxYear, setTaxYear] = useState('');
  const [clientName, setClientName] = useState('');

  const fetchChecklist = useCallback(async (ty) => {
    try {
      const url = ty
        ? `${API_URL}/api/accountant/checklist/${clientId}?tax_year=${ty}`
        : `${API_URL}/api/accountant/checklist/${clientId}`;
      const res = await axios.get(url);
      setItems(res.data.items || []);
      if (!taxYear && res.data.tax_year) setTaxYear(res.data.tax_year);
    } catch (err) {
      toast.error('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  }, [clientId, taxYear]);

  useEffect(() => {
    // Fetch client name
    axios.get(`${API_URL}/api/accountant/clients`).then(res => {
      const c = (res.data.clients || []).find(c => c.user_id === clientId);
      if (c) setClientName(c.name);
    }).catch(() => {});
    fetchChecklist();
  }, [clientId, fetchChecklist]);

  const handleStatusChange = async (key, status) => {
    try {
      await axios.put(`${API_URL}/api/accountant/checklist/${clientId}/${key}`, { status });
      setItems(prev => prev.map(i => i.key === key ? { ...i, status } : i));
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleRequest = async (key, message) => {
    try {
      await axios.post(`${API_URL}/api/accountant/checklist/${clientId}/${key}/request`, { message });
      toast.success('Document request sent to client');
      fetchChecklist(taxYear);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    }
  };

  const received = items.filter(i => i.status === 'received').length;
  const missing = items.filter(i => i.status === 'missing').length;
  const requested = items.filter(i => i.status === 'requested').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="doc-checklist-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/accountant/client/${clientId}`)}
            className="text-slate-400 hover:text-white h-8 w-8" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-sky-400" />
              Document Checklist
            </h1>
            <p className="text-xs text-slate-500">{clientName} — {taxYear}</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => fetchChecklist(taxYear)}
          className="border-slate-600 text-slate-400 hover:text-white h-9" data-testid="refresh-checklist">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh
        </Button>
      </div>

      {/* Summary badges */}
      <div className="flex gap-3 flex-wrap">
        <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-3 py-1">
          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />{received} Received
        </Badge>
        <Badge className="bg-red-500/10 text-red-400 border border-red-500/20 px-3 py-1">
          <AlertCircle className="h-3.5 w-3.5 mr-1.5" />{missing} Missing
        </Badge>
        {requested > 0 && (
          <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/20 px-3 py-1">
            <Clock className="h-3.5 w-3.5 mr-1.5" />{requested} Requested
          </Badge>
        )}
      </div>

      {/* Checklist Items */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-white text-sm flex items-center gap-2">
            <FileText className="h-4 w-4 text-amber-400" />
            Required Documents — {taxYear}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0 space-y-2">
          {items.map(item => (
            <ChecklistItem
              key={item.key}
              item={item}
              onStatusChange={handleStatusChange}
              onRequest={handleRequest}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountantDocChecklist;
